
import { supabase } from "@/integrations/supabase/client";

export type HolidayCampaign = {
  id: string;
  year: number;
  name: string;
  description?: string;
  status: 'draft' | 'scheduled' | 'active' | 'completed';
  communicationType: 'email' | 'card' | 'gift' | 'other';
  startDate?: string;
  endDate?: string;
  createdAt: string;
  updatedAt: string;
};

export type CampaignRecipient = {
  id: string;
  campaignId: string;
  contactId: string;
  contactName: string;
  organizationId: string;
  organizationName: string;
  status: 'pending' | 'sent' | 'failed';
  sentDate?: string;
  giftId?: string;
};

export const fetchHolidayCampaigns = async () => {
  const { data, error } = await supabase
    .from('holiday_communications')
    .select(`
      id, 
      year, 
      communication_type,
      notes,
      status,
      sent_date,
      created_at,
      updated_at,
      contact:contact_id (id, first_name, last_name),
      organization:organizations!holiday_communications_organization_id_fkey (id, name)
    `)
    .order('year', { ascending: false });
  
  if (error) throw error;
  return data;
};

export const createHolidayCampaign = async (campaign: Partial<HolidayCampaign>) => {
  const { data, error } = await supabase
    .from('holiday_communications')
    .insert({
      year: campaign.year,
      communication_type: campaign.communicationType,
      notes: campaign.description,
      status: campaign.status || 'draft'
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateHolidayCampaign = async (id: string, updates: Partial<HolidayCampaign>) => {
  const { data, error } = await supabase
    .from('holiday_communications')
    .update({
      year: updates.year,
      communication_type: updates.communicationType,
      notes: updates.description,
      status: updates.status,
      sent_date: updates.status === 'completed' ? new Date().toISOString() : null
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

// Note: This is intentionally commented out as we need to create this junction table first
// Currently the database doesn't have a holiday_communication_recipients table

// export const addCampaignRecipients = async (campaignId: string, contactIds: string[]) => {
//   const recipients = contactIds.map(contactId => ({
//     holiday_communication_id: campaignId,
//     contact_id: contactId,
//     status: 'pending'
//   }));
// 
//   const { data, error } = await supabase
//     .from('holiday_communication_recipients')
//     .insert(recipients)
//     .select();
// 
//   if (error) throw error;
//   return data;
// };
// 
// export const fetchCampaignRecipients = async (campaignId: string) => {
//   const { data, error } = await supabase
//     .from('holiday_communication_recipients')
//     .select(`
//       id,
//       status,
//       sent_date,
//       contact:contact_id (id, first_name, last_name, email, organization_id),
//       organization:contact(organization_id (id, name))
//     `)
//     .eq('holiday_communication_id', campaignId);
// 
//   if (error) throw error;
//   return data;
// };

// Temporary implementation using the contacts table directly
export const addCampaignRecipients = async (campaignId: string, contactIds: string[]) => {
  // Instead of adding to a junction table, we update the holiday_communication_id on the contacts
  // This is temporary until we can create a proper junction table
  const { data, error } = await supabase
    .from('contacts')
    .update({ last_contacted_date: new Date().toISOString() })
    .in('id', contactIds)
    .select();

  if (error) throw error;
  return data;
};

export const fetchCampaignRecipients = async (campaignId: string) => {
  // Fetch all contacts for now, then filter in the frontend
  // This is temporary until we can create a proper junction table
  const { data, error } = await supabase
    .from('contacts')
    .select(`
      id,
      first_name,
      last_name,
      email,
      organization_id,
      last_contacted_date,
      organization:organizations!contacts_organization_id_fkey (id, name)
    `)
    .order('last_name', { ascending: true });

  if (error) throw error;
  return data;
};
